import React, { useState, useEffect } from 'react';
import { BusinessHours, DaySchedule, PublicHoliday } from '@openmirror/shared';
import { useActualApi } from '../../hooks/useActualApi';
import AfterHoursPromptField from './AfterHoursPromptField';
import PublicHolidaysSettings from './PublicHolidaysSettings';
import { requireBusinessHoursConfig, verifyBusinessHoursSave } from './businessHoursConfig';

const DAYS: (keyof BusinessHours)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_DAY: DaySchedule = {
  isOpen: true,
  start: '09:00',
  end: '17:00'
};

const TIMEZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : [
  'UTC', 'Africa/Johannesburg', 'Europe/London', 'America/New_York', 'Asia/Dubai', 
  'Asia/Singapore', 'Australia/Sydney', 'Europe/Paris', 'America/Los_Angeles'
];

const BusinessHoursSettings: React.FC = () => {
  const { fetchClientConfig, updateClientConfig } = useActualApi();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [afterHoursPrompt, setAfterHoursPrompt] = useState('');
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  const [hours, setHours] = useState<BusinessHours>(() => ({
    monday: { ...DEFAULT_DAY },
    tuesday: { ...DEFAULT_DAY },
    wednesday: { ...DEFAULT_DAY },
    thursday: { ...DEFAULT_DAY },
    friday: { ...DEFAULT_DAY },
    saturday: { ...DEFAULT_DAY },
    sunday: { ...DEFAULT_DAY, isOpen: false },
  }));
  
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const loadConfig = async () => {
        try {
            const result = await fetchClientConfig();
            if (!result.success || !result.config) {
                throw new Error(result.message || 'Failed to load business hours.');
            }
            const config = requireBusinessHoursConfig(result.config);
            if (mounted) {
                setAfterHoursPrompt(config.afterHoursPrompt ?? '');
                setPublicHolidays(config.publicHolidays ?? []);
                if (config.timezone) setTimezone(config.timezone);
                if (config.businessHours) {
                    setHours(prev => ({ ...prev, ...config.businessHours }));
                }
            }
        } catch (e) {
            console.error(e);
            if (mounted) setLoadError(`Unable to load your settings. ${e instanceof Error ? e.message : 'Please reopen this panel to try again.'}`);
        } finally {
            if (mounted) setLoading(false);
        }
    };
    loadConfig();
    return () => { mounted = false; };
  }, [fetchClientConfig]);

  const handleDayChange = (day: keyof BusinessHours, changes: Partial<DaySchedule>) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], ...changes }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setFeedback(null);
    try {
      const result = await updateClientConfig({
            timezone,
            businessHours: hours,
            afterHoursPrompt,
            publicHolidays,
      });
      if (!result.success) throw new Error(result.message || 'Failed to save settings.');
      verifyBusinessHoursSave(result.config, { afterHoursPrompt, publicHolidays });
      setFeedback('Settings saved successfully!');
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Failed to save business hours', error);
      setFeedback(`Error saving settings. ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
        setIsSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading business hours...</div>;
  if (loadError) return <div role="alert" className="p-6 text-red-600">{loadError}</div>;

  return (
    <div className="space-y-6 bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Business Hours & Location</h3>
        <p className="mt-1 text-sm text-gray-500">
          Set your time zone and operating hours. The AI will use this to inform customers if you are currently open.
        </p>
      </div>

      {/* Timezone Selection */}
      <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <div className="sm:col-span-3">
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
            Timezone
          </label>
          <div className="mt-1">
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="space-y-4">
          {DAYS.map((day) => (
            <div key={day} className="flex flex-col sm:flex-row sm:items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center w-32 mb-2 sm:mb-0">
                <input
                  id={`open-${day}`}
                  type="checkbox"
                  checked={hours[day].isOpen}
                  onChange={(e) => handleDayChange(day, { isOpen: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor={`open-${day}`} className="ml-3 block text-sm font-medium text-gray-700 capitalize cursor-pointer">
                  {day}
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <input
                  type="time"
                  disabled={!hours[day].isOpen}
                  value={hours[day].start}
                  onChange={(e) => handleDayChange(day, { start: e.target.value })}
                  className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-400 p-1 border"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  disabled={!hours[day].isOpen}
                  value={hours[day].end}
                  onChange={(e) => handleDayChange(day, { end: e.target.value })}
                  className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-400 p-1 border"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <PublicHolidaysSettings value={publicHolidays} onChange={setPublicHolidays} />
      <AfterHoursPromptField value={afterHoursPrompt} onChange={setAfterHoursPrompt} />

      <div className="flex items-center justify-end pt-6">
        {feedback && (
            <span className={`mr-4 text-sm ${feedback.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {feedback}
            </span>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default BusinessHoursSettings;
